/**
 * Parameters whose result is arithmetic on other results in the same report.
 *
 * MCV is HCT over RBC, globulin is total protein less albumin, and so on. The
 * technician types the measured values; these are derived from them on the
 * entry screen as they type, and again by the server on save, which overwrites
 * whatever the browser sent for them. One implementation, so the screen, the
 * stored row, the import and the demo seed cannot disagree about an MCV.
 *
 * ## Inputs are parameter codes
 *
 * A formula names its inputs by `TestParameter.code` (`HCT`, `RBC`), the one
 * stable identifier the catalogue has, and finds them in the same report. A
 * chained formula names a calculated code: A/G ratio reads `GLOB`, LDL reads
 * `VLDL`. Definitions are declared dependencies-first and evaluated in that
 * order, so a chain resolves in one pass.
 *
 * ## When a value is left blank
 *
 * Silence rather than a guess, exactly as `flagFor` does: a missing or
 * non-numeric input, a result reported against a detection limit (`<0.5` is not
 * a number to divide by), a zero divisor, or a negative answer — every quantity
 * here is non-negative, so a negative one means an input was mistyped. eGFR is
 * also blank outside the population MDRD was validated on: adults, male or
 * female.
 *
 * ## This file imports nothing, deliberately
 *
 * The demo seed runs under Node's type stripping, so the same rule as
 * `pricing.ts` and `reference-range.ts` applies. `Gender` is spelled out below
 * for that reason, and `ParameterFormula` is checked against the Prisma enum in
 * `server/prisma/enum-parity.ts`.
 */

export interface FormulaPatient {
  age_year: number;
  gender: 'male' | 'female' | 'other';
}

interface FormulaDefinition {
  label: string;
  /** How the formula reads, for the entry screen and the parameter form. */
  expression: string;
  /** Parameter codes, in the order `compute` receives their values. */
  inputs: readonly string[];
  decimals: number;
  compute: (values: readonly number[], patient: FormulaPatient) => number | null;
}

const ratio = (top: number, bottom: number, scale = 1): number | null =>
  bottom === 0 ? null : (top / bottom) * scale;

/** A differential percentage as a count per cmm. */
const absolute = (percentCode: string, name: string): FormulaDefinition => ({
  label: `${name} absolute count`,
  expression: `${percentCode} % / 100 × TLC`,
  inputs: [percentCode, 'TLC'],
  decimals: 0,
  compute: ([percent = 0, tlc = 0]) => (percent / 100) * tlc,
});

/**
 * MDRD, with the 186 constant the lab's formula sheet uses (the pre-IDMS
 * calibration), in mL/min/1.73m².
 */
function mdrd([creatinine = 0]: readonly number[], patient: FormulaPatient): number | null {
  if (creatinine <= 0 || patient.age_year < 18 || patient.gender === 'other') return null;
  const sex = patient.gender === 'female' ? 0.742 : 1;
  return 186 * creatinine ** -1.154 * patient.age_year ** -0.203 * sex;
}

/**
 * Friedewald's TG/5 holds below 400 mg/dL; above it the lab's sheet switches to
 * TG × 0.166 (≈ TG/6), which overestimates VLDL less at high triglycerides.
 */
const vldl = ([tg = 0]: readonly number[]): number => (tg < 400 ? tg / 5 : tg * 0.166);

export const PARAMETER_FORMULAS = {
  mcv: {
    label: 'MCV',
    expression: 'HCT / RBC × 10',
    inputs: ['HCT', 'RBC'],
    decimals: 1,
    compute: ([hct = 0, rbc = 0]) => ratio(hct, rbc, 10),
  },
  mch: {
    label: 'MCH',
    expression: 'HB / RBC × 10',
    inputs: ['HB', 'RBC'],
    decimals: 1,
    compute: ([hb = 0, rbc = 0]) => ratio(hb, rbc, 10),
  },
  mchc: {
    label: 'MCHC',
    expression: 'HB / HCT × 100',
    inputs: ['HB', 'HCT'],
    decimals: 1,
    compute: ([hb = 0, hct = 0]) => ratio(hb, hct, 100),
  },
  abs_neut: absolute('NEUT', 'Neutrophils'),
  abs_lymph: absolute('LYMPH', 'Lymphocytes'),
  abs_mid: absolute('MID', 'MID cells'),
  abs_mono: absolute('MONO', 'Monocytes'),
  abs_eos: absolute('EOS', 'Eosinophils'),
  abs_baso: absolute('BASO', 'Basophils'),
  bun: {
    label: 'BUN',
    expression: 'UREA / 2.14',
    inputs: ['UREA'],
    decimals: 1,
    compute: ([urea = 0]) => urea / 2.14,
  },
  egfr_mdrd: {
    label: 'eGFR (MDRD)',
    expression: '186 × CREA^-1.154 × Age^-0.203 × 0.742 if female',
    inputs: ['CREA'],
    decimals: 0,
    compute: mdrd,
  },
  indirect_bilirubin: {
    label: 'Indirect bilirubin',
    expression: 'TBIL − DBIL',
    inputs: ['TBIL', 'DBIL'],
    decimals: 2,
    compute: ([total = 0, direct = 0]) => total - direct,
  },
  globulin: {
    label: 'Globulin',
    expression: 'TP − ALB',
    inputs: ['TP', 'ALB'],
    decimals: 1,
    compute: ([protein = 0, albumin = 0]) => protein - albumin,
  },
  ag_ratio: {
    label: 'A/G ratio',
    expression: 'ALB / GLOB',
    inputs: ['ALB', 'GLOB'],
    decimals: 2,
    compute: ([albumin = 0, globulin = 0]) => ratio(albumin, globulin),
  },
  hdl_chol_ratio: {
    label: 'HDL / Cholesterol ratio',
    expression: 'HDL / CHOL',
    inputs: ['HDL', 'CHOL'],
    decimals: 2,
    compute: ([hdl = 0, chol = 0]) => ratio(hdl, chol),
  },
  vldl: {
    label: 'VLDL',
    expression: 'TG / 5 when TG < 400, else TG × 0.166',
    inputs: ['TG'],
    decimals: 0,
    compute: vldl,
  },
  ldl_friedewald: {
    label: 'LDL (Friedewald)',
    expression: 'CHOL − HDL − VLDL',
    inputs: ['CHOL', 'HDL', 'VLDL'],
    decimals: 0,
    compute: ([chol = 0, hdl = 0, vldlValue = 0]) => chol - hdl - vldlValue,
  },
} as const satisfies Record<string, FormulaDefinition>;

export type ParameterFormula = keyof typeof PARAMETER_FORMULAS;

/** Every formula key, in evaluation order. */
export const PARAMETER_FORMULA_KEYS = Object.keys(PARAMETER_FORMULAS) as [
  ParameterFormula,
  ...ParameterFormula[],
];

/** One row of a report, as the calculator needs it. */
export interface FormulaRow {
  code: string;
  formula: ParameterFormula | null;
  result: string;
}

/**
 * A typed result as a plain number, or null. Stricter than `parseResult`, which
 * accepts `<0.5` — fine for flagging, wrong to compute with.
 */
function plainNumber(text: string): number | null {
  const cleaned = text.trim().replaceAll(',', '');
  if (cleaned === '' || cleaned.startsWith('<') || cleaned.startsWith('>')) return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}

function evaluate(
  formula: FormulaDefinition,
  valueByCode: ReadonlyMap<string, string>,
  patient: FormulaPatient,
): string {
  const values = formula.inputs.map((code) => plainNumber(valueByCode.get(code) ?? ''));
  if (values.some((value) => value === null)) return '';

  const result = formula.compute(values as number[], patient);
  if (result === null || !Number.isFinite(result) || result < 0) return '';
  return result.toFixed(formula.decimals);
}

/**
 * The result of every calculated row in one report, by code.
 *
 * Measured rows are read as typed; calculated rows are computed in registry
 * order and fed back in, so a chain (GLOB → A/G) sees its own inputs. A row
 * that cannot be computed maps to `''`.
 */
export function computeCalculatedResults(
  rows: readonly FormulaRow[],
  patient: FormulaPatient,
): Map<string, string> {
  const valueByCode = new Map(rows.map((row) => [row.code, row.formula ? '' : row.result]));
  const calculated = new Map<string, string>();

  for (const key of PARAMETER_FORMULA_KEYS) {
    for (const row of rows) {
      if (row.formula !== key) continue;
      const result = evaluate(PARAMETER_FORMULAS[key], valueByCode, patient);
      valueByCode.set(row.code, result);
      calculated.set(row.code, result);
    }
  }
  return calculated;
}
